import { Field, Input, Select } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { choMs, laUrlApi } from '../../nghiep-vu/ket-noi';
import type { KetNoiApi } from '../../types';

/** Các ô của một kết nối API — dùng chung cho hộp Thêm nguồn API và Sửa kết nối API (native lặp lại hai lần). */
export interface KhaiApi {
  apiUrl: string;
  xacThuc: KetNoiApi['xacThuc'];
  tenKhoa: string;
  viTriKhoa: 'header' | 'query';
  tenDangNhap: string;
  khoaApi: string;
  choGiay: number;
}

export const KHAI_API_MAC_DINH: KhaiApi = {
  apiUrl: '',
  xacThuc: 'none',
  tenKhoa: 'X-Api-Key',
  viTriKhoa: 'header',
  tenDangNhap: '',
  khoaApi: '',
  choGiay: 25,
};

/** Khối `api` gửi lên. Khoá KHÔNG nằm ở đây — nó đi riêng ở `apiSecret`, vào kho bí mật. */
export function khoiApi(k: KhaiApi): KetNoiApi {
  return {
    baseUrl: k.apiUrl.trim(),
    xacThuc: k.xacThuc,
    tenKhoa: k.tenKhoa.trim() || 'X-Api-Key',
    viTriKhoa: k.viTriKhoa,
    tenDangNhap: k.tenDangNhap.trim(),
    thoiGianChoMs: choMs(k.choGiay),
  };
}

/** Đọc kết nối đã lưu vào các ô. Ô khoá luôn mở ra TRỐNG — khoá là chỉ-ghi. */
export function tuKetNoi(a: KetNoiApi | undefined): KhaiApi {
  if (!a) return KHAI_API_MAC_DINH;
  return {
    apiUrl: a.baseUrl ?? '',
    xacThuc: a.xacThuc ?? 'none',
    tenKhoa: a.tenKhoa || 'X-Api-Key',
    viTriKhoa: a.viTriKhoa ?? 'header',
    tenDangNhap: a.tenDangNhap ?? '',
    khoaApi: '',
    choGiay: Math.round((a.thoiGianChoMs ?? 25_000) / 1000),
  };
}

export function CacOKetNoiApi({
  gt,
  dat,
  giuKhoa,
}: {
  gt: KhaiApi;
  dat: (thay: Partial<KhaiApi>) => void;
  /** Hộp sửa: để trống ô khoá nghĩa là giữ khoá đang lưu. */
  giuKhoa?: boolean;
}) {
  const { t } = useT();
  const saiUrl = gt.apiUrl.trim() !== '' && !laUrlApi(gt.apiUrl);
  return (
    <>
      <Field
        label={t('troLyAiKetNoiThem.apiDiaChiGoc')}
        hint={t('troLyAiKetNoiThem.apiDiaChiGocGhi')}
        error={saiUrl ? t('troLyAiKetNoiThem.apiDiaChiSai') : undefined}
        required
      >
        {(f) => (
          <Input
            {...f}
            placeholder="https://erp.khach.vn/api"
            value={gt.apiUrl}
            onChange={(e) => dat({ apiUrl: e.target.value })}
          />
        )}
      </Field>
      <div className="flex flex-wrap gap-3">
        <Field label={t('troLyAiKetNoiThem.apiXacThuc')} className="w-full sm:w-64">
          {(f) => (
            <Select<KetNoiApi['xacThuc']>
              {...f}
              className="w-full"
              value={gt.xacThuc}
              onChange={(v) => dat({ xacThuc: v })}
              options={[
                { value: 'none', label: t('troLyAiKetNoiThem.apiXacThucNone') },
                { value: 'bearer', label: t('troLyAiKetNoiThem.apiXacThucBearer') },
                { value: 'api_key', label: t('troLyAiKetNoiThem.apiXacThucApiKey') },
                { value: 'basic', label: t('troLyAiKetNoiThem.apiXacThucBasic') },
              ]}
            />
          )}
        </Field>
        <Field
          label={t('troLyAiKetNoiThem.apiThoiGianCho')}
          hint={t('troLyAiKetNoiThem.apiThoiGianChoGhi')}
          className="w-full sm:w-48"
        >
          {(f) => (
            <Input
              {...f}
              type="number"
              min={1}
              max={60}
              value={String(gt.choGiay)}
              onChange={(e) => dat({ choGiay: Number(e.target.value) || 25 })}
            />
          )}
        </Field>
      </div>
      {gt.xacThuc === 'api_key' ? (
        <div className="flex flex-wrap gap-3">
          <Field label={t('troLyAiKetNoiThem.apiTenKhoa')} className="min-w-[14rem] flex-1">
            {(f) => <Input {...f} value={gt.tenKhoa} onChange={(e) => dat({ tenKhoa: e.target.value })} />}
          </Field>
          <Field label={t('troLyAiKetNoiThem.apiViTriKhoa')} className="w-full sm:w-48">
            {(f) => (
              <Select<'header' | 'query'>
                {...f}
                className="w-full"
                value={gt.viTriKhoa}
                onChange={(v) => dat({ viTriKhoa: v })}
                options={[
                  { value: 'header', label: t('troLyAiKetNoiThem.apiViTriHeader') },
                  { value: 'query', label: t('troLyAiKetNoiThem.apiViTriQuery') },
                ]}
              />
            )}
          </Field>
        </div>
      ) : null}
      {gt.xacThuc === 'basic' ? (
        <Field label={t('troLyAiKetNoiThem.apiTenDangNhap')}>
          {(f) => (
            <Input
              {...f}
              autoComplete="off"
              value={gt.tenDangNhap}
              onChange={(e) => dat({ tenDangNhap: e.target.value })}
            />
          )}
        </Field>
      ) : null}
      {gt.xacThuc !== 'none' ? (
        <Field
          label={t('troLyAiKetNoiThem.apiKhoa')}
          hint={giuKhoa ? t('troLyAiKetNoiThem.apiKhoaGiuNguyen') : t('troLyAiKetNoiThem.apiKhoaGhi')}
        >
          {(f) => (
            <Input
              {...f}
              type="password"
              autoComplete="new-password"
              value={gt.khoaApi}
              onChange={(e) => dat({ khoaApi: e.target.value })}
            />
          )}
        </Field>
      ) : null}
    </>
  );
}
